import { useQuery } from '@tanstack/react-query'
import { TASK_STATUS_LABELS, type TaskStatus } from '@netcon-work/shared'
import { Benutzerverwaltung } from '../components/Benutzerverwaltung'
import { PoolVerwaltung } from '../components/PoolVerwaltung'
import { WorkspaceVerwaltung } from '../components/WorkspaceVerwaltung'
import { KIVorlagen } from '../components/KIVorlagen'
import { ProfilAutomatik } from '../components/ProfilAutomatik'
import { KIZugang } from '../components/KIZugang'
import { ClaudeAnbindung } from '../components/ClaudeAnbindung'
import { MailZugang } from '../components/MailZugang'
import { MicrosoftEinrichtung } from '../components/MicrosoftEinrichtung'
import { NetConZugang } from '../components/NetConZugang'
import { Rueckstand } from '../components/Rueckstand'
import { TriageRegeln } from '../components/TriageRegeln'
import { UmgebungEinstellungen } from '../components/UmgebungEinstellungen'
import { Verteilung } from '../components/Verteilung'
import { Topbar } from '../components/Topbar'
import { Card, CardPad, ComingSoon, SectionTitle } from '../components/ui/primitives'
import { cn } from '../lib/cn'
import { useMerker } from '../lib/merker'
import {
  bestandQuery,
  jobFehlerQuery,
  queueQuery,
  rueckstandQuery,
  sitzungQuery,
  syncQuery,
} from '../lib/queries'

/**
 * KI & Einstellungen (Konzept Kap. 5.9) – enthält in Phase 1 die Admin-Sicht auf
 * den Sync (AP 1.5).
 *
 * Leitgedanke: **kein Konnektor darf still ausfallen.** Wer hier hinsieht, muss
 * erkennen, ob die Daten aktuell sind – bevor jemand im Daily Zahlen zeigt, die
 * seit Tagen stehen.
 *
 * Seit AP 2.5 liegt hier auch die **Benutzerverwaltung** (Rollen, Anmeldekonten).
 * Sie kommt in diese Ansicht und bekommt keinen eigenen Eintrag in der
 * Seitenleiste: das Mockup nennt neun Ansichten, und die Navigation zu erweitern
 * wäre eine Änderung an der UI-Referenz – die trifft Nils, nicht die KI.
 */
const BEREICHE = [
  { id: 'stand', label: 'Stand', untertitel: 'Rückstand, Bestand, Konnektoren und ihr Abgleich' },
  {
    id: 'zugaenge',
    label: 'Zugänge',
    untertitel: 'NetCon Tickets, Postfach, Microsoft je Person, KI, Claude',
  },
  { id: 'team', label: 'Team & Welten', untertitel: 'Arbeitswelten, Pools, Benutzer' },
  {
    id: 'eingang',
    label: 'Eingang & KI',
    untertitel: 'Verteilung, Triage-Regeln, Vorlagen, Automatik',
  },
  {
    id: 'umgebungen',
    label: 'Umgebungen',
    untertitel: 'GitLab, GitHub, Coolify, Domains – für Umgebungen an Vorhaben',
  },
] as const
type BereichId = (typeof BEREICHE)[number]['id']
const BEREICH_IDS = BEREICHE.map((b) => b.id) as readonly BereichId[]
const istBereich = (wert: string | null): wert is BereichId =>
  wert !== null && (BEREICH_IDS as readonly string[]).includes(wert)

export function Einstellungen() {
  const { data: sitzung } = useQuery(sitzungQuery)
  /**
   * **Diese Ansicht gehört dem Admin** (24.08.2026, Konzept Kap. 5 Punkt 9).
   *
   * Sieben der neun Blöcke stehen serverseitig auf `rollenProcedure('admin')` – wer als
   * Entwickler hier landete, sah eine Seite aus Überschriften mit nichts darunter und
   * erzeugte dabei ein halbes Dutzend `FORBIDDEN` in der Konsole. Die Seitenleiste zeigt den
   * Punkt seither nicht mehr; dieser Riegel ist für den Weg über die Adresszeile.
   *
   * `sitzung === undefined` heißt *noch nicht geladen* – dann wird weder gefragt noch
   * abgewiesen, sonst blitzte die Absage bei jedem Aufruf kurz auf.
   */
  const darfSehen = sitzung?.role === 'admin'
  const { data: quellen = [] } = useQuery({ ...syncQuery, enabled: darfSehen })
  const { data: bestand } = useQuery({ ...bestandQuery, enabled: darfSehen })
  const { data: queue = {} } = useQuery({ ...queueQuery, enabled: darfSehen })
  const { data: jobFehler = [] } = useQuery({ ...jobFehlerQuery, enabled: darfSehen })
  const { data: rueckstand } = useQuery({ ...rueckstandQuery, enabled: darfSehen })

  /**
   * Der Bereich: gemerkt je Gerät, überstimmt von `?bereich=` in der Adresse – so kann ein
   * Hinweis irgendwo in Work direkt auf „Zugänge" zeigen.
   */
  const [gemerkt, merken] = useMerker<BereichId>('einstellungen-bereich', 'stand', BEREICH_IDS)
  const ausAdresse = new URLSearchParams(window.location.search).get('bereich')
  const bereich: BereichId = istBereich(ausAdresse) ? ausAdresse : gemerkt
  const aktiver = BEREICHE.find((b) => b.id === bereich) ?? BEREICHE[0]!
  const bereichWaehlen = (id: BereichId) => {
    merken(id)
    const adresse = new URL(window.location.href)
    adresse.searchParams.set('bereich', id)
    window.history.replaceState(window.history.state, '', adresse)
    window.scrollTo({ top: 0 })
  }

  if (!darfSehen) {
    return (
      <>
        <Topbar titel="KI & Einstellungen" untertitel="Quellen, Sync-Zustand, Modelle" />
        {sitzung && (
          <Card>
            <CardPad>
              <div className="text-[13px] font-semibold">
                Diese Ansicht ist der Administration vorbehalten.
              </div>
              <p className="mt-1.5 text-xs text-ink2">
                Zugänge, Rollen, Triage-Regeln und die KI-Verwaltung ändern nur Admins (Konzept Kap.
                5). Durchgesetzt wird das im Backend, nicht durch dieses Ausblenden.
              </p>
            </CardPad>
          </Card>
        )}
      </>
    )
  }

  return (
    <>
      <Topbar titel="KI & Einstellungen" untertitel={aktiver.untertitel} />

      {/**
       * **Vier Bereiche statt einer Rolle** (15.09.2026, Nils: *„Können wir «KI & Einstellungen»
       * mal aufräumen … da blickt man bei der langen Liste ja nicht mehr durch"*,
       * [E-352](../../../../docs/ENTSCHEIDUNGEN.md#e-352)).
       *
       * Vierzehn Blöcke untereinander waren die ehrliche Form, solange es fünf waren. Jetzt
       * steht je Bereich eine Frage: *Läuft alles?* (Stand), *Wie kommt Work an fremde
       * Systeme?* (Zugänge), *Wer arbeitet wo?* (Team & Welten), *Was passiert mit dem, was
       * hereinkommt?* (Eingang & KI). Die Reihenfolge innerhalb eines Bereichs ist die alte –
       * sie war je begründet (erst die Welt, dann die Benutzer; erst die Verteilung, dann die
       * Triage), und diese Begründungen stehen noch an Ort und Stelle.
       *
       * Der Bereich wird je Gerät gemerkt und steht in der Adresse (`?bereich=zugaenge`), damit
       * ein Hinweis wie „KI & Einstellungen → Zugänge" ein Link sein kann.
       */}
      <nav
        aria-label="Bereiche der Einstellungen"
        className="sticky unter-leiste z-20 -mx-4 mb-4 border-b border-line bg-page/95 px-4 py-2 backdrop-blur lg:-mx-8 lg:px-8"
      >
        <div role="tablist" className="flex flex-wrap gap-1.5">
          {BEREICHE.map((b) => {
            const aktiv = b.id === bereich
            const zahl =
              b.id === 'stand' ? (rueckstand?.aufgaben.length ?? 0) + jobFehler.length : 0
            return (
              <button
                key={b.id}
                type="button"
                role="tab"
                aria-selected={aktiv}
                aria-controls={`einstellungen-${b.id}`}
                onClick={() => bereichWaehlen(b.id)}
                className={cn(
                  'tap flex cursor-pointer items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                  aktiv ? 'bg-ink text-white' : 'bg-surface text-ink2 hover:bg-[#f3f2ef]',
                )}
              >
                {b.label}
                {zahl > 0 && (
                  <span
                    className={cn(
                      'tabular rounded-full px-1.5 text-[10.5px] leading-4',
                      aktiv ? 'bg-white/20 text-white' : 'bg-pool-sofort text-white',
                    )}
                    title="Rückstand und fehlgeschlagene Jobs"
                  >
                    {zahl}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </nav>

      <div role="tabpanel" id={`einstellungen-${bereich}`}>
        {bereich === 'stand' && (
          <>
            {/**
             * **Der Rückstand steht oben**, seit dem 03.08.2026. Er beantwortet „ist mein
             * Statuswechsel angekommen?" – die Frage, mit der man diese Seite meistens öffnet.
             */}
            <Rueckstand />
            <SectionTitle hint="Was liegt in Work">Bestand</SectionTitle>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Kennzahl
                label="Aufgaben"
                wert={bestand?.jeQuelle.reduce((s, q) => s + q.anzahl, 0) ?? 0}
              />
              <Kennzahl label="Ereignisse" wert={bestand?.ereignisse ?? 0} />
              <Kennzahl label="Herkunftsbezüge" wert={bestand?.bezuege ?? 0} />
              <Kennzahl
                label="Jobs wartend"
                wert={Number(queue.wartend ?? 0)}
                warnung={Number(queue.fehler ?? 0) > 0 ? `${queue.fehler} fehlerhaft` : undefined}
              />
            </div>

            {bestand && bestand.jeStatus.length > 0 && (
              <Card className="mt-4">
                <CardPad className="flex flex-wrap gap-x-6 gap-y-2 text-[12.5px]">
                  {bestand.jeStatus.map((s) => (
                    <span key={s.status}>
                      <span className="text-ink2">
                        {TASK_STATUS_LABELS[s.status as TaskStatus]}
                      </span>{' '}
                      <strong className="tabular">{s.anzahl}</strong>
                    </span>
                  ))}
                </CardPad>
              </Card>
            )}

            <SectionTitle hint="Konnektoren und ihr Abgleich">Quellen</SectionTitle>
            <div className="grid gap-3">
              {quellen.map((quelle) => (
                <Card key={quelle.id}>
                  <CardPad>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[13.5px] font-bold">{quelle.label}</span>
                      <span className="font-mono text-[11.5px] text-muted">{quelle.id}</span>
                      <Marke an={quelle.isActive} text={quelle.isActive ? 'aktiv' : 'inaktiv'} />
                      <Marke an={false} text={quelle.isWritable ? 'schreibend' : 'nur lesend'} />
                      {quelle.bereiche.length > 0 && (
                        <span className="ml-auto text-[11.5px] text-muted">
                          {quelle.bereiche.length} Bereiche
                        </span>
                      )}
                    </div>

                    {quelle.aeltesterAbgleichMinuten !== null && (
                      <div className="mt-2.5">
                        <Aktualitaet minuten={quelle.aeltesterAbgleichMinuten} />
                        {quelle.fehlerGesamt > 0 && (
                          <span className="ml-3 text-[12px] font-semibold text-pool-sofort">
                            {quelle.fehlerGesamt} Fehler seit Beginn
                          </span>
                        )}
                      </div>
                    )}

                    {quelle.bereiche.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="min-w-[560px] mt-3 w-full border-collapse text-[12px]">
                          <thead>
                            <tr>
                              {['Bereich', 'letzter Abgleich', 'Fehler'].map((h) => (
                                <th
                                  key={h}
                                  className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {quelle.bereiche.map((b) => (
                              <tr key={b.scope}>
                                <td className="border-b border-line px-2 py-1.5 font-mono text-[11.5px]">
                                  {b.scope.replace('epcan-main/netcon/', '')}
                                </td>
                                <td className="border-b border-line px-2 py-1.5">
                                  {b.alterMinuten === null ? (
                                    <span className="text-muted">noch nie</span>
                                  ) : (
                                    <Aktualitaet minuten={b.alterMinuten} klein />
                                  )}
                                </td>
                                <td className="tabular border-b border-line px-2 py-1.5">
                                  {b.errorCount > 0 ? (
                                    <span
                                      className="text-pool-sofort"
                                      title={b.lastError ?? undefined}
                                    >
                                      {b.errorCount}
                                    </span>
                                  ) : (
                                    <span className="text-muted">–</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardPad>
                </Card>
              ))}
            </div>

            {jobFehler.length > 0 && (
              <>
                <SectionTitle hint="Jobs, die aufgegeben haben">Fehlgeschlagen</SectionTitle>
                <Card>
                  <CardPad className="py-2">
                    {jobFehler.map((job) => (
                      <div
                        key={job.id}
                        className="border-b border-line py-2 text-[12px] last:border-b-0"
                      >
                        <div className="font-mono font-semibold">{job.name}</div>
                        <div className="text-muted">
                          {job.attempts} von {job.maxAttempts} Versuchen ·{' '}
                          {job.lastError?.slice(0, 160)}
                        </div>
                      </div>
                    ))}
                  </CardPad>
                </Card>
              </>
            )}
          </>
        )}

        {bereich === 'zugaenge' && (
          <>
            <SectionTitle hint="Zugangsdaten und Warteschlangen der Ticket-API">
              NetCon Tickets
            </SectionTitle>
            <NetConZugang />

            {/**
             * **Der Mailversand steht unter den Zugängen und über den Benutzern** – er ist ein
             * Zugang zu einem fremden System wie NetCon und die KI, kein Stammdatum. Wer die
             * Seite von oben liest, richtet erst die Wege nach draußen ein.
             */}
            <SectionTitle hint="Postfach für Benachrichtigungen – Erwähnungen in Kommentaren">
              Mailversand
            </SectionTitle>
            <MailZugang />
            {/* Persönliche Verbindungen zu Outlook und To Do – dieselbe App-Registrierung, andere Berechtigungen (Z-17). */}
            <MicrosoftEinrichtung />

            <SectionTitle hint="Anbieter, Schlüssel, Standardmodell, Budget">
              KI-Verwaltung
            </SectionTitle>
            <KIZugang />

            <SectionTitle hint="claude.ai und Claude Code an freigegebene Aufgaben (Z-21)">
              Claude-Anbindung
            </SectionTitle>
            <ClaudeAnbindung />
          </>
        )}

        {bereich === 'team' && (
          <>
            {/**
             * **Die Workspaces stehen über den Benutzern** (Z-07). Wer jemanden anlegt, teilt ihn
             * anschließend zu – und dafür muss es die Arbeitswelt schon geben. Wer die Seite von
             * oben liest, richtet in der Reihenfolge ein, in der es funktioniert.
             */}
            <SectionTitle hint="Arbeitswelten anlegen und Menschen zuteilen">
              Workspaces
            </SectionTitle>
            <WorkspaceVerwaltung />

            {/** Die Pools stehen unter den Workspaces – sie gehoeren zu einem, nicht umgekehrt. */}
            <SectionTitle hint="Voraete dieser Arbeitswelt – Reihenfolge, „geht vor“, Vorrat">
              Pools
            </SectionTitle>
            <PoolVerwaltung />

            <SectionTitle hint="Rollen, Anmeldekonten, aktiv/inaktiv">Benutzer</SectionTitle>
            <Benutzerverwaltung />
          </>
        )}

        {bereich === 'umgebungen' && <UmgebungEinstellungen />}

        {bereich === 'eingang' && (
          <>
            {/**
             * **Die Verteilung steht vor der Triage** – und zwar in derselben Reihenfolge, in der beim
             * Import gearbeitet wird: erst entscheidet sich, in **welche** Arbeitswelt etwas geht, dann
             * ordnen die Regeln **dieser** Welt es in einen Pool ein. Wer die Seite von oben liest,
             * richtet in der Reihenfolge ein, in der es läuft.
             */}
            <SectionTitle hint="Wohin geht, was hereinkommt – Ziel je Quelle und Regeln">
              Verteilung
            </SectionTitle>
            <Verteilung />

            <SectionTitle hint="Vorsortierung im Eingang – vor der KI">Triage-Regeln</SectionTitle>
            <TriageRegeln />

            {/**
             * **Die Vorlagen stehen unter dem Zugang** – sie brauchen ihn. Ohne hinterlegten
             * Schlüssel ist eine gepflegte Anweisung ein Text ohne Wirkung, und wer von oben nach
             * unten liest, richtet erst das eine ein und dann das andere.
             */}
            <KIVorlagen />

            {/**
             * **Die Profil-Automatik unter den Vorlagen** – sie ist der zweite Verbraucher des
             * Zugangs und der einzige, der **von selbst** läuft. Deshalb steht sie hier und nicht
             * bei den Benutzern: wer eine Automatik sucht, die Geld ausgibt, sucht sie bei der KI.
             */}
            <ProfilAutomatik />
            {/**
             * **Was hier noch fehlt, steht als Platzhalter da und nicht als leere Fläche.**
             * „Modell je Funktion" braucht mehr als eine Funktion, „Verbrauch und Übernahmequote"
             * brauchen Aufrufe, die es noch nicht gab. Beides erst zu bauen, wenn es etwas zu
             * zeigen gibt, ist billiger als eine Ansicht, die Nullen anzeigt und erklärt werden muss.
             */}
            <ComingSoon phase="Phase 6 (KI-Triage)">
              Modell je Funktion, Fallback-Kette, Verbrauch und Übernahmequote – sobald die erste
              Funktion Aufrufe erzeugt
            </ComingSoon>
          </>
        )}
      </div>
    </>
  )
}

function Kennzahl({
  label,
  wert,
  warnung,
}: {
  label: string
  wert: number
  warnung?: string | undefined
}) {
  return (
    <Card>
      <div className="px-[17px] py-3.5">
        <div className="text-[11.5px] font-medium text-ink2">{label}</div>
        <div className="tabular mt-[3px] text-[25px] font-bold tracking-[-0.5px]">
          {wert.toLocaleString('de-DE')}
        </div>
        {warnung && <div className="text-[11.5px] font-semibold text-pool-sofort">{warnung}</div>}
      </div>
    </Card>
  )
}

/**
 * Alter des letzten Abgleichs – mit Ampel.
 *
 * Ein reiner Zeitstempel lädt zum Übersehen ein. „vor 4 Tagen" in Rot fällt auf.
 * Schwellen: bis 15 Min normal (Polling läuft alle 5), bis 2 Std auffällig,
 * darüber ein Problem.
 */
function Aktualitaet({ minuten, klein }: { minuten: number; klein?: boolean }) {
  const stufe = minuten <= 15 ? 'gut' : minuten <= 120 ? 'warn' : 'schlecht'
  const farbe = {
    gut: 'text-good-text',
    warn: 'text-warn',
    schlecht: 'text-pool-sofort',
  }[stufe]
  const punkt = { gut: 'bg-good', warn: 'bg-warn', schlecht: 'bg-pool-sofort' }[stufe]

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-semibold ${farbe} ${klein ? 'text-[11.5px]' : 'text-[12.5px]'}`}
    >
      <i className={`size-[7px] rounded-full ${punkt}`} />
      {beschreibeAlter(minuten)}
    </span>
  )
}

function beschreibeAlter(minuten: number): string {
  if (minuten < 1) return 'gerade eben'
  if (minuten < 60) return `vor ${minuten} Min`
  const stunden = Math.round(minuten / 60)
  if (stunden < 24) return `vor ${stunden} Std`
  const tage = Math.round(stunden / 24)
  return `vor ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`
}

function Marke({ an, text }: { an: boolean; text: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-[9px] py-0.5 text-[10.5px] font-bold ${
        an ? 'bg-[#e5f7f0] text-[#0d7a52]' : 'bg-[#f0efec] text-[#6d6b65]'
      }`}
    >
      {text}
    </span>
  )
}
